/**
 * Impersonation Utilities
 * Handles OAuth token exchange for tenant and user impersonation
 */

import { authConfig } from '@/config/auth.config';

// Session storage keys
const IMPERSONATION_KEY = 'motr_impersonating';
const ORIGINAL_TOKENS_KEY = 'motr_original_tokens';
const IMPERSONATION_TYPE_KEY = 'motr_impersonation_type'; // 'tenant', 'user', or 'delegation'

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
  token_type: string;
  scope?: string;
}

/**
 * Decode a JWT token to extract its payload claims
 * Note: This does NOT validate the token, just decodes it
 */
const decodeJwt = (token: string): Record<string, unknown> | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Decode the payload (middle part)
    const payload = parts[1];
    // Handle URL-safe base64 encoding
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64);
    return JSON.parse(decoded);
  } catch {
    console.error('[Impersonation] Failed to decode JWT');
    return null;
  }
};

/**
 * Check if currently impersonating a tenant
 */
export const isImpersonating = (): boolean => {
  return sessionStorage.getItem(IMPERSONATION_KEY) === 'true';
};

/**
 * Get the tenant ID being impersonated (stored in session)
 */
export const getImpersonatedTenantId = (): string | null => {
  return sessionStorage.getItem('motr_impersonated_tenant_id');
};

/**
 * Get the tenant name being impersonated (stored in session)
 */
export const getImpersonatedTenantName = (): string | null => {
  return sessionStorage.getItem('motr_impersonated_tenant_name');
};

/**
 * Get the type of impersonation ('tenant', 'user', or 'delegation')
 */
export const getImpersonationType = (): 'tenant' | 'user' | 'delegation' | null => {
  return sessionStorage.getItem(IMPERSONATION_TYPE_KEY) as 'tenant' | 'user' | 'delegation' | null;
};

/**
 * Get the user ID being impersonated (stored in session)
 */
export const getImpersonatedUserId = (): string | null => {
  return sessionStorage.getItem('motr_impersonated_user_id');
};

/**
 * Get the user name being impersonated (stored in session)
 */
export const getImpersonatedUserName = (): string | null => {
  return sessionStorage.getItem('motr_impersonated_user_name');
};

/**
 * Get the delegated user name (the delegator's name, stored in session)
 */
export const getDelegatedUserName = (): string | null => {
  return sessionStorage.getItem('motr_delegated_user_name');
};

/**
 * Impersonate a user in a specific tenant
 * Uses OAuth 2.0 Impersonation grant type
 * ABP requires the token to be passed as a form parameter, not as an Authorization header
 */
export const impersonateTenant = async (
  tenantId: string,
  tenantName: string,
  userName: string = 'admin',
  currentAccessToken: string
): Promise<void> => {
  // Get the authority URL (remove trailing slash if present)
  const authority = authConfig.authority.replace(/\/$/, '');

  // Build the token request
  // ABP's impersonation grant type requires the access_token as a form parameter
  const formData = new URLSearchParams();
  formData.append('grant_type', 'Impersonation');
  formData.append('client_id', authConfig.client_id);
  formData.append('scope', authConfig.scope);
  formData.append('access_token', currentAccessToken); // ABP requires access_token as form parameter
  formData.append('TenantId', tenantId);
  formData.append('TenantUserName', userName);

  // Make the token request
  // Include __tenant header to set tenant context for the impersonation
  const response = await fetch(`${authority}/connect/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      '__tenant': tenantId,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'Impersonation failed';
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error_description || errorJson.error || errorMessage;
    } catch {
      if (errorText) {
        errorMessage = errorText;
      }
    }
    throw new Error(errorMessage);
  }

  const tokenResponse: TokenResponse = await response.json();

  // Debug: Log the token response to verify impersonation worked
  console.log('[Impersonation] Token response received:', {
    hasAccessToken: !!tokenResponse.access_token,
    expiresIn: tokenResponse.expires_in,
    tokenType: tokenResponse.token_type,
  });

  // Try both with and without trailing slash for the storage key
  const authorityWithSlash = authority.endsWith('/') ? authority : `${authority}/`;
  const authorityNoSlash = authority.replace(/\/$/, '');

  // Find the current user storage key (could be with or without trailing slash)
  let storageKey = `oidc.user:${authorityWithSlash}:${authConfig.client_id}`;
  let currentUser = localStorage.getItem(storageKey);

  if (!currentUser) {
    storageKey = `oidc.user:${authorityNoSlash}:${authConfig.client_id}`;
    currentUser = localStorage.getItem(storageKey);
  }

  // Store the original tokens for later restoration
  if (currentUser) {
    sessionStorage.setItem(ORIGINAL_TOKENS_KEY, currentUser);
    sessionStorage.setItem('motr_original_storage_key', storageKey);
  }

  // Mark as impersonating (tenant type)
  sessionStorage.setItem(IMPERSONATION_KEY, 'true');
  sessionStorage.setItem(IMPERSONATION_TYPE_KEY, 'tenant');
  sessionStorage.setItem('motr_impersonated_tenant_id', tenantId);
  sessionStorage.setItem('motr_impersonated_tenant_name', tenantName);

  // Update the stored user with new tokens
  if (currentUser) {
    try {
      const userObj = JSON.parse(currentUser);
      userObj.access_token = tokenResponse.access_token;
      if (tokenResponse.refresh_token) {
        userObj.refresh_token = tokenResponse.refresh_token;
      }
      // Update expiration
      const now = Math.floor(Date.now() / 1000);
      userObj.expires_at = now + tokenResponse.expires_in;

      // CRITICAL FIX: Update profile.sub with the impersonated user's ID
      // The access_token is a JWT containing the impersonated user's claims
      const accessTokenClaims = decodeJwt(tokenResponse.access_token);
      if (accessTokenClaims) {
        console.log('[Impersonation] Decoded access token claims:', {
          sub: accessTokenClaims.sub,
          name: accessTokenClaims.name,
          email: accessTokenClaims.email,
        });

        // Update the profile with impersonated user's claims
        if (!userObj.profile) {
          userObj.profile = {};
        }
        if (accessTokenClaims.sub) {
          userObj.profile.sub = accessTokenClaims.sub;
        }
        if (accessTokenClaims.name) {
          userObj.profile.name = accessTokenClaims.name;
        }
        if (accessTokenClaims.email) {
          userObj.profile.email = accessTokenClaims.email;
        }
        // Also update given_name and family_name if present
        if (accessTokenClaims.given_name) {
          userObj.profile.given_name = accessTokenClaims.given_name;
        }
        if (accessTokenClaims.family_name) {
          userObj.profile.family_name = accessTokenClaims.family_name;
        }
        // Update preferred_username from the token
        if (accessTokenClaims.preferred_username) {
          userObj.profile.preferred_username = accessTokenClaims.preferred_username;
        }
        // If name claim is missing, construct it from given_name and family_name
        if (!accessTokenClaims.name && (accessTokenClaims.given_name || accessTokenClaims.family_name)) {
          const constructedName = `${accessTokenClaims.given_name || ''} ${accessTokenClaims.family_name || ''}`.trim();
          if (constructedName) {
            userObj.profile.name = constructedName;
            console.log('[Impersonation] Constructed name from given_name + family_name:', constructedName);
          }
        }
      }

      // Store the updated user object
      localStorage.setItem(storageKey, JSON.stringify(userObj));

      // Also store direct tokens like Angular does for compatibility
      localStorage.setItem('access_token', tokenResponse.access_token);
      if (tokenResponse.refresh_token) {
        localStorage.setItem('refresh_token', tokenResponse.refresh_token);
      }
    } catch {
      throw new Error('Failed to update authentication tokens');
    }
  }

  // Force reload with cache clear to ensure fresh state
  window.location.href = '/';
};

/**
 * Impersonate a user within the same tenant
 * Uses OAuth 2.0 Impersonation grant type with UserId parameter
 * Requires AbpIdentity.Users.Impersonation permission
 */
export const impersonateUser = async (
  userId: string,
  userName: string,
  currentAccessToken: string
): Promise<void> => {
  // Get the authority URL (remove trailing slash if present)
  const authority = authConfig.authority.replace(/\/$/, '');

  // Build the token request for user impersonation
  // ABP's impersonation grant type requires the access_token as a form parameter
  const formData = new URLSearchParams();
  formData.append('grant_type', 'Impersonation');
  formData.append('client_id', authConfig.client_id);
  formData.append('scope', authConfig.scope);
  formData.append('access_token', currentAccessToken); // ABP requires access_token as form parameter
  formData.append('UserId', userId); // UserId instead of TenantId for user impersonation

  // Make the token request
  const response = await fetch(`${authority}/connect/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'User impersonation failed';
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error_description || errorJson.error || errorMessage;
    } catch {
      if (errorText) {
        errorMessage = errorText;
      }
    }
    throw new Error(errorMessage);
  }

  const tokenResponse: TokenResponse = await response.json();

  // Debug: Log the token response to verify impersonation worked
  console.log('[User Impersonation] Token response received:', {
    hasAccessToken: !!tokenResponse.access_token,
    expiresIn: tokenResponse.expires_in,
    tokenType: tokenResponse.token_type,
  });

  // Try both with and without trailing slash for the storage key
  const authorityWithSlash = authority.endsWith('/') ? authority : `${authority}/`;
  const authorityNoSlash = authority.replace(/\/$/, '');

  // Find the current user storage key (could be with or without trailing slash)
  let storageKey = `oidc.user:${authorityWithSlash}:${authConfig.client_id}`;
  let currentUser = localStorage.getItem(storageKey);

  if (!currentUser) {
    storageKey = `oidc.user:${authorityNoSlash}:${authConfig.client_id}`;
    currentUser = localStorage.getItem(storageKey);
  }

  // Store the original tokens for later restoration
  if (currentUser) {
    sessionStorage.setItem(ORIGINAL_TOKENS_KEY, currentUser);
    sessionStorage.setItem('motr_original_storage_key', storageKey);
  }

  // Mark as impersonating (user type)
  sessionStorage.setItem(IMPERSONATION_KEY, 'true');
  sessionStorage.setItem(IMPERSONATION_TYPE_KEY, 'user');
  sessionStorage.setItem('motr_impersonated_user_id', userId);
  sessionStorage.setItem('motr_impersonated_user_name', userName);

  // Update the stored user with new tokens
  if (currentUser) {
    try {
      const userObj = JSON.parse(currentUser);
      userObj.access_token = tokenResponse.access_token;
      if (tokenResponse.refresh_token) {
        userObj.refresh_token = tokenResponse.refresh_token;
      }
      // Update expiration
      const now = Math.floor(Date.now() / 1000);
      userObj.expires_at = now + tokenResponse.expires_in;

      // Update profile with the impersonated user's claims from the new access token
      const accessTokenClaims = decodeJwt(tokenResponse.access_token);
      if (accessTokenClaims) {
        console.log('[User Impersonation] Decoded access token claims:', {
          sub: accessTokenClaims.sub,
          name: accessTokenClaims.name,
          email: accessTokenClaims.email,
        });

        // Update the profile with impersonated user's claims
        if (!userObj.profile) {
          userObj.profile = {};
        }
        if (accessTokenClaims.sub) {
          userObj.profile.sub = accessTokenClaims.sub;
        }
        if (accessTokenClaims.name) {
          userObj.profile.name = accessTokenClaims.name;
        }
        if (accessTokenClaims.email) {
          userObj.profile.email = accessTokenClaims.email;
        }
        if (accessTokenClaims.given_name) {
          userObj.profile.given_name = accessTokenClaims.given_name;
        }
        if (accessTokenClaims.family_name) {
          userObj.profile.family_name = accessTokenClaims.family_name;
        }
        if (accessTokenClaims.preferred_username) {
          userObj.profile.preferred_username = accessTokenClaims.preferred_username;
        }
        // If name claim is missing, construct it from given_name and family_name
        if (!accessTokenClaims.name && (accessTokenClaims.given_name || accessTokenClaims.family_name)) {
          const constructedName = `${accessTokenClaims.given_name || ''} ${accessTokenClaims.family_name || ''}`.trim();
          if (constructedName) {
            userObj.profile.name = constructedName;
            console.log('[User Impersonation] Constructed name from given_name + family_name:', constructedName);
          }
        }
      }

      // Store the updated user object
      localStorage.setItem(storageKey, JSON.stringify(userObj));

      // Also store direct tokens for compatibility
      localStorage.setItem('access_token', tokenResponse.access_token);
      if (tokenResponse.refresh_token) {
        localStorage.setItem('refresh_token', tokenResponse.refresh_token);
      }
    } catch {
      throw new Error('Failed to update authentication tokens');
    }
  }

  // Force reload with cache clear to ensure fresh state
  window.location.href = '/';
};

/**
 * Login as a delegated user via Authority Delegation
 * Uses OAuth 2.0 Impersonation grant type with UserDelegationId parameter
 * This does NOT require AbpIdentity.Users.Impersonation permission
 */
export const delegatedLogin = async (
  userDelegationId: string,
  delegatorUserName: string,
  currentAccessToken: string
): Promise<void> => {
  const authority = authConfig.authority.replace(/\/$/, '');

  // Build the token request - uses same Impersonation grant type
  // but with UserDelegationId instead of UserId or TenantId
  const formData = new URLSearchParams();
  formData.append('grant_type', 'Impersonation');
  formData.append('client_id', authConfig.client_id);
  formData.append('scope', authConfig.scope);
  formData.append('access_token', currentAccessToken);
  formData.append('UserDelegationId', userDelegationId);

  const response = await fetch(`${authority}/connect/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = 'Delegated login failed';
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error_description || errorJson.error || errorMessage;
    } catch {
      if (errorText) errorMessage = errorText;
    }
    throw new Error(errorMessage);
  }

  const tokenResponse: TokenResponse = await response.json();

  console.log('[Delegation] Token response received:', {
    hasAccessToken: !!tokenResponse.access_token,
    expiresIn: tokenResponse.expires_in,
  });

  // --- Token storage (same pattern as impersonateUser) ---
  const authorityWithSlash = authority.endsWith('/') ? authority : `${authority}/`;
  const authorityNoSlash = authority.replace(/\/$/, '');

  let storageKey = `oidc.user:${authorityWithSlash}:${authConfig.client_id}`;
  let currentUser = localStorage.getItem(storageKey);
  if (!currentUser) {
    storageKey = `oidc.user:${authorityNoSlash}:${authConfig.client_id}`;
    currentUser = localStorage.getItem(storageKey);
  }

  // Backup original tokens
  if (currentUser) {
    sessionStorage.setItem(ORIGINAL_TOKENS_KEY, currentUser);
    sessionStorage.setItem('motr_original_storage_key', storageKey);
  }

  // Mark as impersonating (delegation type)
  sessionStorage.setItem(IMPERSONATION_KEY, 'true');
  sessionStorage.setItem(IMPERSONATION_TYPE_KEY, 'delegation');
  sessionStorage.setItem('motr_delegated_user_name', delegatorUserName);
  sessionStorage.setItem('motr_delegation_id', userDelegationId);

  // Update stored user with new tokens
  if (currentUser) {
    try {
      const userObj = JSON.parse(currentUser);
      userObj.access_token = tokenResponse.access_token;
      if (tokenResponse.refresh_token) {
        userObj.refresh_token = tokenResponse.refresh_token;
      }
      const now = Math.floor(Date.now() / 1000);
      userObj.expires_at = now + tokenResponse.expires_in;

      // Update profile from JWT claims
      const accessTokenClaims = decodeJwt(tokenResponse.access_token);
      if (accessTokenClaims) {
        if (!userObj.profile) userObj.profile = {};
        if (accessTokenClaims.sub) userObj.profile.sub = accessTokenClaims.sub;
        if (accessTokenClaims.name) userObj.profile.name = accessTokenClaims.name;
        if (accessTokenClaims.email) userObj.profile.email = accessTokenClaims.email;
        if (accessTokenClaims.given_name) userObj.profile.given_name = accessTokenClaims.given_name;
        if (accessTokenClaims.family_name) userObj.profile.family_name = accessTokenClaims.family_name;
        if (accessTokenClaims.preferred_username) userObj.profile.preferred_username = accessTokenClaims.preferred_username;
        if (!accessTokenClaims.name && (accessTokenClaims.given_name || accessTokenClaims.family_name)) {
          userObj.profile.name = `${accessTokenClaims.given_name || ''} ${accessTokenClaims.family_name || ''}`.trim();
        }
      }

      localStorage.setItem(storageKey, JSON.stringify(userObj));
      localStorage.setItem('access_token', tokenResponse.access_token);
      if (tokenResponse.refresh_token) {
        localStorage.setItem('refresh_token', tokenResponse.refresh_token);
      }
    } catch {
      throw new Error('Failed to update authentication tokens');
    }
  }

  // Reload to apply new identity
  window.location.href = '/';
};

/**
 * End impersonation and return to original user
 */
export const endImpersonation = (): void => {
  // Restore original tokens using the stored key
  const originalTokens = sessionStorage.getItem(ORIGINAL_TOKENS_KEY);
  const originalStorageKey = sessionStorage.getItem('motr_original_storage_key');

  if (originalTokens && originalStorageKey) {
    localStorage.setItem(originalStorageKey, originalTokens);

    // Restore direct tokens
    try {
      const userObj = JSON.parse(originalTokens);
      localStorage.setItem('access_token', userObj.access_token);
      if (userObj.refresh_token) {
        localStorage.setItem('refresh_token', userObj.refresh_token);
      }
    } catch {
      // Ignore parse errors
    }
  }

  // Clear all impersonation state (both tenant and user)
  sessionStorage.removeItem(IMPERSONATION_KEY);
  sessionStorage.removeItem(IMPERSONATION_TYPE_KEY);
  sessionStorage.removeItem(ORIGINAL_TOKENS_KEY);
  sessionStorage.removeItem('motr_original_storage_key');
  // Clear tenant impersonation keys
  sessionStorage.removeItem('motr_impersonated_tenant_id');
  sessionStorage.removeItem('motr_impersonated_tenant_name');
  // Clear user impersonation keys
  sessionStorage.removeItem('motr_impersonated_user_id');
  sessionStorage.removeItem('motr_impersonated_user_name');
  // Clear delegation keys
  sessionStorage.removeItem('motr_delegated_user_name');
  sessionStorage.removeItem('motr_delegation_id');

  // Reload the page to apply original tokens
  window.location.href = '/';
};
